import { CommonModule } from '@angular/common';
import { Component, inject, Signal } from '@angular/core';
import { ErrorHandlerService } from '../../../../_shared/services/error-handler.service';
import { FuelLevelItem } from '../../models/fuel-level.model';
import { FuelLevelService } from '../../services/fuel-level.service';
import { FuelLevelDisplayComponent } from '../fuel-level-display/fuel-level-display.component';
import { Subscription, Subject, takeUntil } from 'rxjs';
import { FilterService } from '../../../../_shared/services';
import { FuelLevelStateService } from '../../services/fuel-level-state.service';
import { ScrollingModule } from '@angular/cdk/scrolling';
import { NgbTooltip } from '@ng-bootstrap/ng-bootstrap';
import { ErrorStateComponent } from "../../../../_shared/components/states/error-state/error-state.component";
import { EmptyStateComponent } from '../../../../_shared/components/states/empty-state/empty-state.component';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';

@Component({
  selector: 'app-low-fuel-level-alerts',
  standalone: true,
  imports: [FuelLevelDisplayComponent, CommonModule, ScrollingModule, NgbTooltip, ErrorStateComponent, TranslocoDirective],
  templateUrl: './low-fuel-level-alerts.component.html',
  styleUrl: './low-fuel-level-alerts.component.scss'
})
export class LowFuelLevelAlertsComponent {
  private fuelLevelService: FuelLevelService = inject(FuelLevelService);
  private errorHandlerService: ErrorHandlerService = inject(ErrorHandlerService);
  private filterService: FilterService = inject(FilterService);
  private stateService: FuelLevelStateService = inject(FuelLevelStateService);
  private transloco: TranslocoService = inject(TranslocoService);

  private filterSubscription!: Subscription;
  private cancelSubject: Subject<void> = new Subject();
  private destroySubject: Subject<void> = new Subject<void>();

  public isLoading: boolean = true;
  public hasError: boolean = false;
  public fuelLevels: FuelLevelItem[] = [];
  public filteredFuelLevels: FuelLevelItem[] = [];
  public isFuelLevelLoading: Signal<boolean> = this.stateService.isLoading;

  ngOnInit(): void {
    this.filterService.filterUpdated$
      .pipe(takeUntil(this.destroySubject))
      .subscribe((filter) => {
        this.loadFuelLevels(filter.accountIds);
      });

    this.filterService.searchTerm$
      .pipe(takeUntil(this.destroySubject))
      .subscribe((searchTerm) => {
        if (searchTerm) {
          this.filteredFuelLevels = [...this.fuelLevels.filter(x => x.assetName.toLowerCase().includes(searchTerm.toLowerCase()))];
        } else {
          this.filteredFuelLevels = [...this.fuelLevels];
        }
      });
  }

  ngOnDestroy(): void {
    this.filterSubscription?.unsubscribe();
    this.cancelSubject.next();
    this.cancelSubject.complete();
    this.destroySubject.next();
    this.destroySubject.complete();
  }

  async loadFuelLevels(accountIds: number[]): Promise<void> {
    try {
      this.isLoading = true;
      this.hasError = false;
      this.cancelSubject.next();
      const response = await this.fuelLevelService.getLowFuelLevelsAsync(accountIds, this.cancelSubject);
      this.fuelLevels = response.data ?? [];
      this.filteredFuelLevels = [...this.fuelLevels];
      this.isLoading = false;
    } catch (ex) {
      this.errorHandlerService.handleError(ex, {
        callback: () => {
          this.hasError = true;
          this.isLoading = false;
        }
      });
    }
  }

  async process(fuelLevel: FuelLevelItem): Promise<void> {
    try {
      fuelLevel.isLoading = true;
      await this.fuelLevelService.processLowFuelLevelAsync(fuelLevel.id);
      this.removeItem(fuelLevel.id);
      this.stateService.lowFuelLevelSelectedSubject$.next(fuelLevel);
      this.isLoading = false;
    } catch (e) {
      this.errorHandlerService.handleError(e, {
        callback: () => {
          this.isLoading = false;
        },
        showToast: true
      });
    }
  }

  getDistanceToEmpty(distanceToEmpty: number): string {
    return distanceToEmpty ? `${distanceToEmpty}km` : this.transloco.translate('fuelLevel.common.notAvailable')
  }

  private removeItem(fuelLevelId: number): void {
    this.fuelLevels = [...this.fuelLevels.filter(x => x.id !== fuelLevelId)];
    this.filteredFuelLevels = [...this.fuelLevels];
  }
}